import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  AiOutlineEdit, 
  AiOutlineArrowLeft
} from 'react-icons/ai';
import { FaUsers, FaUserTimes, FaUserTie } from 'react-icons/fa';
import { MdAdminPanelSettings } from 'react-icons/md';
import { toast } from "react-toastify";
import { useAuth } from '../../context/Auth';
import LoadingSpinner from '../../components/Loading/LoadingSpinner';
import GroupMembers from '../CellManagement/GroupMembers';
import GroupExecutives from '../CellManagement/GroupExecutives';


const ChurchGroupMembers = () => {
  const groupId = localStorage.getItem("selectedGroupId");
  const navigate = useNavigate();
  const { requestWithAuth } = useAuth();

  
  // Group state
  const [group, setGroup] = useState(null);
  const [loadingGroup, setLoadingGroup] = useState(true);
  
  const [activeTab, setActiveTab] = useState('members'); 

  const fetchGroupDetails = async () => {
    try {
      setLoadingGroup(true);
      const response = await requestWithAuth(`/groups/church-groups/${groupId}/`, 'GET');
      setGroup(response.data);
    } catch (error) {
      toast.error("Failed to load group details");
    } finally {
      setLoadingGroup(false);
    }
  };

  useEffect(() => {
    fetchGroupDetails();
  }, [groupId]);

  

  if (loadingGroup) {
    return (
      <div className="min-h-screen bg-gray-50/50 p-6 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <LoadingSpinner />
          <p className="text-gray-600">Loading group details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/group-management')}
          className="p-2 hover:bg-white rounded-lg transition-colors border border-gray-200"
        >
          <AiOutlineArrowLeft className="text-xl text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Group Details</h1>
          <p className="text-gray-600">Manage group information, members, and executives</p>
        </div>
      </div>

      {/* Group Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            {/* Group Logo */}
            <div className="flex-shrink-0">
              {group?.group_logo ? (
                <img
                  src={group.group_logo}
                  alt={group.group_name}
                  className="w-20 h-20 rounded-xl object-cover border border-gray-200"
                />
              ) : (
                <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
                  <FaUsers className="text-white text-2xl" />
                </div>
              )}
            </div>

            {/* Group Details */}
            <div className="flex-grow">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{group?.group_name}</h2>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
                      {group?.group_category}
                    </span>
                    <span className="text-sm text-gray-500">
                      Created {new Date(group?.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {group?.description && (
                    <p className="text-gray-600 mb-4">{group.group_description}</p>
                  )}
                </div>
                {/* <button
                  onClick={() => navigate(`/groups/${groupId}/edit`)}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <AiOutlineEdit className="text-lg" />
                  Edit Group
                </button> */}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{group?.total_members || 0}</p>
                  <p className="text-sm text-gray-600">Total Members</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{group?.active_members || 0}</p>
                  <p className="text-sm text-gray-600">Active</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{group?.inactive_members || 0}</p>
                  <p className="text-sm text-gray-600">Inactive</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{group?.active_executives || 0}</p>
                  <p className="text-sm text-gray-600">Executives</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('members')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'members'
                  ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FaUsers className="text-lg" />
                Group Members
              </div>
            </button>
            <button
              onClick={() => setActiveTab('executives')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'executives'
                  ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FaUserTie className="text-lg" />
                Group Executives
              </div>
            </button>
          </nav>
        </div>

        {/* Members Tab Content */}
        {activeTab === 'members' && (
          <GroupMembers activeTab={activeTab} groupId={groupId} group={group} fetchGroupDetails={fetchGroupDetails} />
        )}

        {/* Executives Tab Content */}
        {activeTab === 'executives' && (
          <GroupExecutives groupId={groupId} />
        )}
      </div>

      

      
    </div>
  );
};

export default ChurchGroupMembers;